import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ErrorState, LoadingState } from "@/components/ui/StateBoundaries";
import * as approvalsApi from "@/lib/approvalsApi";
import BillReview, { billArithmetic } from "@/components/approvals/BillReview";
import { PayloadView, StockItemsReview, SupplierReview } from "@/components/approvals/OtherReviews";
import { STATUS_TONE, ageText, money, typeLabel } from "@/components/approvals/shared";

const EDITABLE = new Set(["bill_bundle", "purchase_bill", "create_stock_items", "create_supplier"]);
const BILLS = new Set(["bill_bundle", "purchase_bill"]);

/** What an approved bill did, in the owner's words: the bill, the stock and the money. */
export function ApprovalResult({ result }) {
  if (!result) return null;
  const isBill = result.purchase_order_id;
  return (
    <div className="rounded-md border border-success/30 bg-success-subtle p-4 space-y-2" data-testid="approval-result" role="status">
      <h3 className="text-body font-semibold text-success">Approved{isBill ? " — the bill is posted" : ""}</h3>
      {isBill ? (
        <>
          <p className="text-body" data-testid="result-bill">Purchase bill <strong>{result.po_number || `#${result.purchase_order_id}`}</strong> for ₹{money(result.total)}</p>
          {result.stock_added?.length > 0 && (
            <div data-testid="result-stock">
              <p className="text-meta font-medium">Stock added</p>
              <ul className="text-body list-disc pl-5">
                {result.stock_added.map((s, i) => <li key={i}>{s.name || `Item #${s.stock_item_id}`}: {s.quantity} {s.unit}</li>)}
              </ul>
            </div>
          )}
          <p className="text-body" data-testid="result-money">
            {result.payment
              ? <>Paid ₹{money(result.payment.amount)} by {String(result.payment.paid_from || result.payment.mode).replaceAll("_", " ")}{result.payable > 0 ? `; ₹${money(result.payable)} still payable` : "; nothing owed"}</>
              : <>Payable to the supplier: ₹{money(result.payable ?? result.total)}</>}
          </p>
          {result.new_items_created > 0 && <p className="text-meta">{result.new_items_created} new stock item(s) created.</p>}
        </>
      ) : (
        <pre className="text-meta whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
      )}
    </div>
  );
}

export default function ApprovalDetail({ id, token, onBack, onDecided }) {
  const [action, setAction] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const data = await approvalsApi.getAction(id, token);
      setAction(data);
      setDraft(JSON.parse(JSON.stringify(data.payload)));
      setFailure("");
    } catch (e) {
      setLoadError(approvalsApi.errorText(e));
    }
  }, [id, token]);

  useEffect(() => { setAction(null); setResult(null); load(); }, [load]);

  const dirty = useMemo(() => action && draft && JSON.stringify(draft) !== JSON.stringify(action.payload), [action, draft]);
  const pending = action?.status === "pending";
  const balanced = useMemo(() => {
    if (!draft || !BILLS.has(action?.action_type)) return true;
    const bill = action.action_type === "bill_bundle" ? draft.bill : draft;
    return Math.abs(billArithmetic(bill).difference) <= 1;
  }, [action, draft]);

  if (loadError) return <ErrorState title="Could not open this request" message={loadError} onRetry={load} />;
  if (!action || !draft) return <LoadingState message="Opening the request…" />;

  const type = action.action_type;
  const editable = pending && EDITABLE.has(type) && !result;

  const approve = async () => {
    setConfirming(false);
    setBusy(true);
    setFailure("");
    try {
      const done = await approvalsApi.approveAction(action.id, dirty ? draft : null, token);
      setResult(done.result);
      toast.success(`Request #${action.id} approved`);
      onDecided?.();
      setAction({ ...action, status: "approved" });
    } catch (e) {
      // The action stays pending: say why, on this screen, and let the approver fix it.
      setFailure(approvalsApi.errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) { setReasonError("Say why you are rejecting this."); return; }
    setBusy(true);
    try {
      await approvalsApi.rejectAction(action.id, reason.trim(), token);
      toast.success(`Request #${action.id} rejected`);
      setRejecting(false);
      onDecided?.();
      await load();
    } catch (e) {
      setReasonError(approvalsApi.errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const body = (() => {
    if (BILLS.has(type)) return <BillReview actionId={action.id} type={type} draft={draft} setDraft={setDraft} context={action.context} token={token} readOnly={!editable} />;
    if (type === "create_stock_items") return <StockItemsReview draft={draft} original={action.payload} setDraft={setDraft} readOnly={!editable} />;
    if (type === "create_supplier" || type === "update_supplier") return <SupplierReview type={type} draft={draft} setDraft={setDraft} context={action.context} readOnly={!editable} />;
    return <PayloadView payload={action.payload} context={action.context} />;
  })();

  return (
    <div className="flex flex-col gap-4" data-testid="approval-detail">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="approval-back"><ArrowLeft /> All requests</Button>
        <h2 className="text-lede font-semibold">#{action.id} · {typeLabel(type)}</h2>
        <Badge className={`h-auto min-h-0 w-fit self-center border-transparent ${STATUS_TONE[action.status] || ""}`} data-testid="approval-status">{action.status}</Badge>
        <span className="text-meta text-muted-foreground">from {action.requested_by} · {ageText(action.requested_at)}</span>
      </div>
      <p className="text-body" data-testid="approval-summary">{action.summary}</p>

      {failure && (
        <div className="rounded-md border border-danger/30 bg-danger-subtle p-3 text-body text-danger" role="alert" data-testid="approval-error">
          <strong>Not approved — nothing was posted.</strong> {failure}
          <p className="text-meta mt-1">The request is still waiting. Fix what is named above, or reject it.</p>
        </div>
      )}
      {!failure && pending && action.last_error && (
        <div className="rounded-md border border-warning/30 bg-warning-subtle p-3 text-meta text-warning" data-testid="approval-last-error">
          An earlier approval failed: {action.last_error}
        </div>
      )}
      {action.status === "rejected" && (
        <div className="rounded-md border bg-muted p-3 text-body" data-testid="approval-rejected">Rejected: {action.decision_note || "no reason recorded"}</div>
      )}
      <ApprovalResult result={result || (action.status === "approved" ? action.result_ref : null)} />

      {body}

      {pending && !result && (
        <div className="sticky bottom-0 -mx-1 flex flex-wrap items-center justify-end gap-2 border-t bg-background/95 px-1 py-3 backdrop-blur" data-testid="approval-actions">
          {dirty && <span className="mr-auto text-meta text-warning" data-testid="approval-edited">You changed this request. Your edits are applied and logged when you approve.</span>}
          <Button variant="outline" disabled={busy} onClick={() => { setReason(""); setReasonError(""); setRejecting(true); }} data-testid="approval-reject"><X /> Reject</Button>
          <Button variant="action" disabled={busy || !balanced} onClick={() => setConfirming(true)} data-testid="approval-approve"><Check /> {busy ? "Approving…" : "Approve"}</Button>
        </div>
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent data-testid="approve-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve request #{action.id}?</AlertDialogTitle>
            <AlertDialogDescription>
              {BILLS.has(type) ? "This posts the bill, adds the stock and records the payable or payment, as you." : "This makes the change now, as you."}
              {dirty ? " Your edits are used instead of what the assistant staged." : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction variant="action" onClick={approve} data-testid="approve-confirm-yes">Approve</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={rejecting} onOpenChange={setRejecting}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject request #{action.id}</DialogTitle>
            <DialogDescription>The reason is kept with the request so the assistant and the team can see it.</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={e => { setReason(e.target.value); setReasonError(""); }} placeholder="Why is this being rejected?" rows={3} data-testid="reject-reason" />
          {reasonError && <p className="text-meta text-danger" role="alert" data-testid="reject-error">{reasonError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(false)}>Cancel</Button>
            <Button variant="destructive" disabled={busy} onClick={reject} data-testid="reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
